import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';
import React, { useEffect, useState } from 'react';

import { Button } from '@components/Button';
import { Input } from '@components/Input';
import { OrdersListPage } from '@components/OrdersListPage';
import { ScreenContent } from '@components/ScreenContent';
import { Typography } from '@components/Typography';
import { logger } from '@lib/logger';
import { useHistoryStore } from '@stores/historyOrders';
import { useTranslation } from '@providers/I18n';

export const OrdersHistoryScreen: React.FC = () => {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const [hasError, setHasError] = useState(false);

  const {
    orders,
    hasMore,
    loading,
    fetch: fetchHistoryOrders,

    searchForOrders,
    searchByPhone,
    reset,
  } = useHistoryStore();

  useEffect(() => {
    reset();
    fetchHistoryOrders(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadMoreOrders = () => {
    const nextPage = page + 1;
    fetchHistoryOrders(nextPage, searchByPhone);
    setPage(nextPage);
  };

  const schema = yup
    .object()
    .shape({
      phone: yup.string().typeError('Phone should be 11 digits.'),
    })
    .required();
  const { control } = useForm({
    defaultValues: {
      phone: '',
    },
    resolver: yupResolver(schema),
  });

  const onSearch = async (phone: string) => {
    try {
      setHasError(false);
      if (phone && !/^[0-9٠-٩]*$/.test(phone)) {
        setHasError(true);
      }
      if ((phone.length === 11 || phone.length === 0) && !hasError) {
        searchForOrders(1, phone);
      }
    } catch (err) {
      logger.log('err', err);
    }
  };

  return (
    <ScreenContent>
      <OrdersListPage
        ordersList={orders}
        loading={loading}
        type="History"
        title={t('orderHistory.header', 'Orders History')}
        search={
          <Controller
            control={control}
            render={({ field: { onChange, value } }) => (
              <>
                <Input
                  onChange={(v) => {
                    onChange(v);
                    onSearch(v);
                  }}
                  value={value || ''}
                  placeholder={t(
                    'orderHistory.search.phone.placeholder',
                    'Search by phone',
                  )}
                  onSubmitEditing={() => onSearch(value || '')}
                />
                <Typography
                  weight="regular"
                  size="sm"
                  variant={hasError ? 'error' : undefined}>
                  {t(
                    'orderHistory.search.phone.message',
                    'Phone number should be 11 digits.',
                  )}
                </Typography>
              </>
            )}
            name="phone"
          />
        }
        noOrdersMessage={t('orderHistory.noOrderHistoryAtTheMoment', {
          defaultValue: 'No orders history at the moment',
        })}
        loadedAllOrdersMessage={t('orderHistory.loadedAllOrdersMessage', {
          defaultValue: 'Loaded all ({{ordersCount}}) orders history',
          ordersCount: orders.length,
        })}
        loadMoreButton={
          <Button
            variant="primary"
            onPress={() => loadMoreOrders()}
            disabled={!hasMore}>
            {t('orderView.loadMoreBtn', {
              defaultValue: 'Load More',
            })}
          </Button>
        }
      />
    </ScreenContent>
  );
};
